export type PasswordStrength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: "Very weak" | "Weak" | "Medium" | "Strong" | "Very strong";
  suggestions: string[];
};

const hasLowercase = (s: string): boolean => /[a-z]/.test(s);
const hasUppercase = (s: string): boolean => /[A-Z]/.test(s);
const hasDigit = (s: string): boolean => /\d/.test(s);
const hasSymbol = (s: string): boolean => /[^A-Za-z0-9]/.test(s);
const hasNoRepeatingChars = (s: string): boolean => !/(.)\1{2,}/.test(s);
const hasNoCommonWords = (s: string): boolean => {
  const lowered = s.toLowerCase();
  const common = ["password", "qwerty", "abc123", "letmein", "admin"];
  return !common.some((w) => lowered.includes(w));
};

export function evaluatePasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return {
      score: 0,
      label: "Very weak",
      suggestions: ["Use at least 8 characters with letters, numbers, and symbols."],
    };
  }

  let score = 0;
  const suggestions: string[] = [];

  const length = password.length;
  const lengthScore = length >= 16 ? 2 : length >= 12 ? 1.5 : length >= 8 ? 1 : 0;

  const varietyCount =
    (hasLowercase(password) ? 1 : 0) +
    (hasUppercase(password) ? 1 : 0) +
    (hasDigit(password) ? 1 : 0) +
    (hasSymbol(password) ? 1 : 0);

  const varietyScore = [0, 0.5, 1, 1.5, 2][varietyCount];

  const repetitionScore = hasNoRepeatingChars(password) ? 0.5 : 0;
  const commonWordPenalty = hasNoCommonWords(password) ? 0.5 : -1;

  const rawScore = lengthScore + varietyScore + repetitionScore + commonWordPenalty;

  score = rawScore <= 0.5 ? 0 : rawScore <= 1.5 ? 1 : rawScore <= 2.5 ? 2 : rawScore <= 3.5 ? 3 : 4;

  if (length < 8) suggestions.push("Use at least 8 characters.");
  if (!hasUppercase(password)) suggestions.push("Add uppercase letters.");
  if (!hasLowercase(password)) suggestions.push("Add lowercase letters.");
  if (!hasDigit(password)) suggestions.push("Add numbers.");
  if (!hasSymbol(password)) suggestions.push("Add special characters.");
  if (!hasNoRepeatingChars(password)) suggestions.push("Avoid repeating characters like 'aaa' or '111'.");
  if (!hasNoCommonWords(password)) suggestions.push("Avoid common words or patterns.");

  const label =
    score === 0
      ? "Very weak"
      : score === 1
      ? "Weak"
      : score === 2
      ? "Medium"
      : score === 3
      ? "Strong"
      : "Very strong";

  return { score: score as PasswordStrength["score"], label, suggestions };
}


